import { useState, useEffect, useMemo, forwardRef, useRef } from 'react';
import MuiAutocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import translator from '@Components/Utilities/Translator';

const Combobox = forwardRef((props, ref) => {
    const defaultFilterOptions = createFilterOptions();
    const popperRef = useRef(null);
    const [isOpen, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState(optionsProp || []);
    const [inputValue, setInputValue] = useState('');
    const [popperOpacity, setPopperOpacity] = useState(1);

    // Function to handle end adornment to keep existing end adornment and add loading indicator when loading
    const endAdorn = (endAdornment) => {
        const newChildren = React.Children.map(endAdornment?.props?.children || [], c => c);

        if (isLoading) {
            newChildren?.unshift(<CircularProgress key="loading-spinner" color='inherit' size={20} />);
        }

        const c = React.cloneElement(endAdornment ? endAdornment :
            <div
                style={{
                    position: 'absolute',
                    right: '9px',
                    top: 'calc(50% - 16px)'
                }}
            />,
            endAdornment?.props,
            newChildren);
        return c;
    };

    const styles = {
        bgVariant: null,
        fontWeight: {
            normal: 400,
            highlight: 700
        },
        listbox: {
            maxHeight: 500,
            border: '1px solid #ccc'
        },
        ...props.styles
    };

    /* MUI - Popper Props */
    const popperOptions = {
        placement: 'bottom-start',
        disablePortal: popperDisablePortal,
        styles: popperStyles,

        ...props.popperOptions
    }

    //props destructuring with defaults
    const {
        width,
        textFieldStyles,
        isOptionEqualToValue,
        disableClearable,
        disabled,
        autoSelect = false,
        clearOnEscape,
        filterOptions = (options, state) => {
            if (options?.some(op => op.isParent)) {
                let filteredOps = defaultFilterOptions(options, state);
                let valsToKeep = [];

                for (var i = 0; i < filteredOps.length; i++) {
                    valsToKeep.push(filteredOps[i].value);

                    let current = filteredOps[i];
                    let seen = new Set();

                    while (current?.parentValue && !seen.has(current.parentValue)) {
                        seen.add(current.parentValue);
                        valsToKeep.push(current.parentValue);
                        current = options.find(op => op.value === current.parentValue);
                    }
                }

                return options.filter(op =>
                    op.isDefault || op.value === "" || valsToKeep.includes(op.value)
                );
            }

            return defaultFilterOptions(options, state);
        },
        freeSolo = false,
        fullWidth = (width != null ? false : true),
        getOptionLabel = (option) => option.value || option,
        id: idProp,
        loadingText = translator.translate('sandhills.loading'),
        onChange = (event, newValue) => {
            setOptions(newValue ? [newValue, ...options] : options);
        },
        options: optionsProp,
        renderOption = (optionProps, option) => {
            if (option?.isParent) {
                return (
                    <li {...optionProps} option={option} {...(option.key ? { key: option.key } : {})}>
                        <b>{getOptionLabel(option)}</b>
                    </li>
                );
            } else {
                return (
                    <li {...optionProps} option={option} {...(option.key ? { key: option.key } : {})}>
                        {getOptionLabel(option)}
                    </li>
                );
            }
        },
        size = 'small',
        value: valueProp,

        /* MUI - Textbox Props */
        onFocus,
        label = '',
        name: nameProp,
        helperText,
        onBlur,
        variant,
        InputProps,
        popperDisablePortal,
        popperStyles,
        onOpen,
        ListboxComponent,
        ListboxProps
    } = props;

    useEffect(() => {
        setOptions(optionsProp || []);
    }, [optionsProp]);

    // Reset options visibility when open state changes
    useEffect(() => {
        if (isOpen) {
            setPopperOpacity(1);
        }
    }, [isOpen]);

    const onInputChange = (event, newInputValue) => {
        if (event?.key != 'Enter') {
            setInputValue(newInputValue);
        }
    };

    const handleBlur = (event) => {
        setOpen(false);
        if (onBlur) {
            onBlur(event);
        }
        return;

        // Start collapsing animation for both options and popper
        setPopperOpacity(0);
    };

    const CustomPopper = (popperProps) => {
        // Store the reference to the popper element
        const { style, ...other } = popperProps;

        return (
            <Popper
                {...other}
                ref={popperRef}
                disablePortal={popperOptions.disablePortal}
                placement={popperOptions.placement}
                style={{
                    ...style,
                    opacity: popperOpacity,
                    transition: 'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                    zIndex: popperStyles?.zIndex ?? 999999,
                    ...(popperStyles || {})
                }}
            />
        );
    };

    const renderInput = (params) => {
        return (
            <TextField
                {...params}
                className='autocomplete-textfield'
                name={nameProp}
                label={label}
                variant={variant}
                onBlur={handleBlur}
                onFocus={onFocusHandler}
                helperText={helperText}
                error={helperText ? true : false}
                inputRef={ref}
                disabled={props.disabled}
                InputProps={{
                    ...params.InputProps,
                    startAdornment: params.InputProps.startAdornment ? params.InputProps.startAdornment : null,
                    endAdornment: useMemo(() => endAdorn(params.InputProps.endAdornment), [isLoading, params.InputProps.endAdornment, inputValue]),
                    style: {
                        ...textFieldStyles,
                        caretColor: 'inherit', // Hide cursor when listening
                        backgroundColor: styles.bgVariant
                    },
                    disableUnderline: props.disableUnderline ? true : false,
                }}
            />
        );
    };

    const onOpenHandler = () => {
        // Ensure options are visible and popper is opaque for animation
        setPopperOpacity(1);

        if (onOpen && (!options || options.length <= 0 || (options.length == 1 && options[0].isDefault || options.length == 2 && options[1].isDefault))) {
            setIsLoading(true);
            onOpen?.()?.then(() => {
                setOpen(true);
                setIsLoading(false);
            })?.catch(() => {
                setOpen(true);
                setIsLoading(false);
            });
        }
        else {
            setOpen(true);
        }
    };

    const onFocusHandler = (event) => {
        try { onFocus && onFocus(event); } catch (err) { }
    }

    return (
        <>
            <MuiAutocomplete
                className='autocomplete'
                autoSelect={autoSelect}
                clearOnEscape={clearOnEscape}
                filterOptions={filterOptions}
                freeSolo={freeSolo}
                fullWidth={fullWidth}
                getOptionLabel={getOptionLabel}
                id={idProp}
                loading={isLoading}
                loadingText={loadingText}
                onChange={onChange}
                onClose={(e, r) => {
                    setOpen(false);
                }}
                onInputChange={onInputChange}
                onOpen={onOpenHandler}
                open={isOpen}
                options={options}
                openOnFocus={props.openOnFocus}
                renderInput={renderInput}
                renderOption={renderOption}
                {...(ListboxComponent ? { ListboxComponent } : {})}
                ListboxProps={{
                    style: {
                        maxHeight: styles.listbox.maxHeight,
                        border: styles.listbox.border,
                        ...ListboxProps?.style,
                    },
                    ...ListboxProps,
                }}
                size={size}
                value={valueProp}
                sx={{ width: width }}
                disableClearable={disableClearable}
                disabled={disabled}
                isOptionEqualToValue={isOptionEqualToValue}
                slots={{ popper: CustomPopper }}
                PopperComponent={CustomPopper}
                slotProps={{
                    paper: {
                        elevation: 0
                    }
                }}
            />
        </>
    );
});

export default Combobox;